import { ChangeEvent, ComponentProps, useCallback, useRef } from 'react';

import { Autocomplete } from 'components/Autocomplete/Autocomplete';
import { ClearButton } from 'components/Input/ClearButton';
import { Input } from 'components/Input/Input';
import { VisuallyHidden } from 'components/VisuallyHidden';
import { SearchType } from 'modules/search/types/SearchType';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { matchSize } from 'utils/responsive';
import { truncateToUTF8Bytes } from 'utils/string/truncateToUTF8Bytes';

type Props = {
  id: string;
  initialQuery: string;
  onChange?: (value: string) => void;
  onClearQuery?: () => void;
  placeholder?: string;
  popularSearches?: string[];
  presentation: ComponentProps<typeof Input>['styleVariant'];
  qaId: string;
  selectedType?: SearchType;
  xsPlaceholder?: string;
};

export function UncontrolledSearchQueryInputContainer({
  id,
  initialQuery,
  onChange,
  onClearQuery,
  placeholder,
  popularSearches,
  presentation,
  qaId,
  selectedType,
  xsPlaceholder,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const clearQuery = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    onClearQuery?.();
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onClearQuery]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const eventValue = event.target.value;
      const value = truncateToUTF8Bytes(eventValue, 512);

      if (inputRef.current && value !== eventValue) {
        inputRef.current.value = value;
      }

      onChange?.(value);
    },
    [onChange],
  );

  const onSuggestionSelected = (suggestion: string) =>
    trackClicked('Popular Searches', {
      search_term: suggestion,
      selected_listing_type: selectedType,
    });

  return (
    <>
      <VisuallyHidden>
        <label htmlFor={id}>{placeholder}</label>
      </VisuallyHidden>
      <Autocomplete
        title={getText('Popular Searches')}
        suggestions={popularSearches || []}
        shouldShowSuggestions={(inputValue) => inputValue.length === 0}
        onSuggestionSelected={onSuggestionSelected}
        openOnFocus
      >
        <Input
          key={initialQuery}
          inputProps={{
            ref: inputRef,
            autoComplete: 'off',
            id,
            name: 'q',
            placeholder: matchSize({ xs: true }) ? xsPlaceholder : placeholder,
            defaultValue: initialQuery,
            onChange: handleChange,
            qaId,
          }}
          suffix={
            <ClearButton
              ariaLabel="Clear search input"
              onClear={clearQuery}
              qaId="search-input-clear"
            />
          }
          hideSuffixWhenEmpty
          styleVariant={presentation}
          affixVariant="transparent"
          fullHeight
        />
      </Autocomplete>
    </>
  );
}
